import { getUserDetails, makeGetCall, makePostCall } from './helper';

/**
 * Whether the reporting workspace's LEFT rail — the study queue (`pane-studies`) — starts
 * collapsed. Radiologists working a single study, or one who keeps the queue on a second
 * monitor, close it once and expect it to stay closed; before this it re-opened on every
 * sign-off that spawned a fresh reporting tab.
 *
 * Deliberately scoped to the studies rail only. The middle "Study Details" pane
 * (`detailsCollapsed`) stays session-local — it holds the report history and the status
 * actions, so re-opening it expanded on each study is the safer default.
 */
export type ReportingPanelPrefs = {
  studiesCollapsed: boolean;
};

export const DEFAULT_REPORTING_PANELS: ReportingPanelPrefs = {
  studiesCollapsed: false,
};

/**
 * Stored in the generic `user_preferences` table (up_username, up_type, up_value JSONB)
 * that already backs mouse actions, favourite tools, hotkeys, panel position and the
 * workstation layout — so no migration and no new column, just a new up_type. Stored as an
 * object so further reporting-layout settings can join it without another preference type.
 */
const PREFERENCE_TYPE = 'reporting_panels';

/**
 * localStorage is ONLY a synchronous cache of the row above, never the source of truth.
 * The reporting page needs the value at first render to pick the rail's initial width — an
 * async read would render expanded and then snap shut in front of the user. index.js
 * refills this from the database on every boot (a full page load follows every login), and
 * logout's localStorage.clear() drops it.
 *
 * Keyed by username so a shared reporting workstation can never serve one radiologist's
 * layout to whoever logs in next.
 */
const STORAGE_KEY = 'pacs_reporting_panels';

const currentUsername = (): string => {
  try {
    const user = getUserDetails();
    return user?.username || user?.user_name || '';
  } catch {
    return '';
  }
};

const asPrefs = (value: any): ReportingPanelPrefs | null =>
  value && typeof value === 'object' && typeof value.studiesCollapsed === 'boolean'
    ? { studiesCollapsed: value.studiesCollapsed }
    : null;

const readCache = (): Record<string, ReportingPanelPrefs> => {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    const parsed = raw ? JSON.parse(raw) : null;
    return parsed && typeof parsed === 'object' ? parsed : {};
  } catch {
    // Corrupt JSON, or storage blocked on a locked-down browser profile.
    return {};
  }
};

const writeCache = (username: string, prefs: ReportingPanelPrefs): void => {
  try {
    window.localStorage.setItem(
      STORAGE_KEY,
      JSON.stringify({ ...readCache(), [username]: prefs })
    );
  } catch (e) {
    console.warn('reportingPanelPref: failed to write localStorage cache', e);
  }
};

/** Synchronous read, safe to call from a useState initialiser. */
export const getReportingPanelPrefs = (): ReportingPanelPrefs => {
  const username = currentUsername();
  if (!username) return DEFAULT_REPORTING_PANELS;

  return asPrefs(readCache()[username]) || DEFAULT_REPORTING_PANELS;
};

/**
 * Hydrate the cache from the database. Called from index.js before the app mounts, so the
 * reporting page's first render already reflects the stored choice. Safe no-op when nobody
 * is logged in.
 */
export const preloadReportingPanels = async (): Promise<ReportingPanelPrefs | null> => {
  const username = currentUsername();
  if (!username) return null;

  try {
    const resp = await makeGetCall(
      `/get-user-preference?username=${encodeURIComponent(username)}&type=${PREFERENCE_TYPE}`
    );
    const prefs = asPrefs(resp?.data?.data);

    // A successful fetch is authoritative: mirror exactly what the row holds (the default
    // when there is no row yet) so a preference cleared server-side cannot linger here.
    writeCache(username, prefs || DEFAULT_REPORTING_PANELS);
    return prefs;
  } catch (e) {
    // Offline or the endpoint failed — keep whatever the cache already holds.
    console.warn('reportingPanelPref: preload failed, using cached/default panels', e);
    return null;
  }
};

/**
 * Persist the rail state. Writes the cache first so a reporting tab opened a moment later
 * honours the new value even while the request is still in flight. Fire-and-forget: a
 * failed save costs the user nothing this session, it only fails to follow them to the
 * next machine, which is not worth a toast mid-report.
 */
export const setStudiesRailCollapsed = async (collapsed: boolean): Promise<boolean> => {
  const username = currentUsername();
  if (!username) return false;

  const prefs: ReportingPanelPrefs = { ...getReportingPanelPrefs(), studiesCollapsed: collapsed };
  writeCache(username, prefs);

  try {
    await makePostCall('/save-user-preference', {
      username,
      type: PREFERENCE_TYPE,
      value: prefs,
    });
    return true;
  } catch (e) {
    console.error('reportingPanelPref: failed to persist panel preference', e);
    return false;
  }
};
